import { create } from 'zustand';
import { getCached, setCache, invalidateCache as invalidateCacheEntry } from './generationCacheHelpers.js';
import { listVisionBoardGenerations } from '../services/visionGenerationsClient.js';

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

export const useVisionBoardStore = create((set, get) => ({
  generationDataCache: {},  // { [projectId]: { generations, fetchedAt } }
  pendingGenerations: [],   // global — survives page navigation
  isLoading: false,

  loadGenerations: async (projectId) => {
    const cache = getCached(get().generationDataCache, projectId);
    const pending = get().pendingGenerations.filter((p) => p.projectId === projectId);

    // Return cached if fresh and no stale pending
    if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS && pending.length === 0) {
      return cache.generations;
    }

    set({ isLoading: true });
    try {
      const generations = await listVisionBoardGenerations({ projectId });
      set((state) => ({
        isLoading: false,
        generationDataCache: setCache(state.generationDataCache, projectId, { generations }),
      }));
      return generations;
    } catch (error) {
      console.error('[VisionBoardStore] Load error:', error);
      set({ isLoading: false });
      return cache?.generations || [];
    }
  },

  getCachedGenerations: (projectId) => {
    return getCached(get().generationDataCache, projectId)?.generations || null;
  },

  updateCachedGenerations: (projectId, generations) => {
    set((state) => ({
      generationDataCache: setCache(state.generationDataCache, projectId, { generations }),
    }));
  },

  invalidateCache: (projectId) => {
    set((state) => ({
      generationDataCache: invalidateCacheEntry(state.generationDataCache, projectId),
    }));
  },

  addPendingGeneration: (data) => {
    set((state) => {
      if (state.pendingGenerations.some((p) => p.generationId === data.generationId)) {
        return state;
      }
      return {
        pendingGenerations: [{ ...data, startTime: Date.now() }, ...state.pendingGenerations],
      };
    });
  },

  removePendingGeneration: (generationId) => {
    set((state) => ({
      pendingGenerations: state.pendingGenerations.filter((p) => p.generationId !== generationId),
    }));
  },

  getPendingForProject: (projectId) => {
    return get().pendingGenerations.filter((p) => p.projectId === projectId);
  },

  reset: () => {
    set({ generationDataCache: {}, pendingGenerations: [], isLoading: false });
  },
}));
